import { ISelectOption } from '@private/components';
import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { FILTER_OPERATIONS } from 'components/Grids/DataGrid/Filters/utils/types';
import { IOutputChannelPayload } from 'components/OutputChannelSidebar/types';
import {
  ICopyMerchantForm,
  IMerchantForm,
} from 'routes/pages/AuthorizedStack/Merchants/Merchant/Form/helpers/types';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import {
  IAllPageFilters,
  IBaseGist,
  IGroupEditPayload,
  INullable,
  IPageType,
  TObject,
} from 'utils/types';

import { normalizeChannel } from '../notification/normalizers';
import { IOutputChannel } from '../notification/types';

import {
  IMerchantData,
  normalizeCascadingSchema,
  normalizeCascadingSchemaDetails,
  normalizeCustomAction,
  normalizedApiUser,
  normalizeGroupNames,
  normalizeMerchant,
  normalizeMerchantClientsLimits,
  normalizeMerchantDetails,
  normalizeMerchantOutputChannelsList,
  normalizeMerchantRulesets,
  normalizeMerchantsNames,
  normalizeMerchantsSelectScope,
  normalizeMerchantsTreeScope,
  normalizeNames,
  normalizeRoutingSchema,
  normalizeRoutingSchemas,
} from './normalizers';
import {
  IActivateCascadingSchemaPayload,
  IActivateRoutingSchemaPayload,
  IApiUser,
  IApiUserEdit,
  ICascadingSchema,
  ICascadingSchemaDetails,
  ICascadingSchemaPayload,
  ICreateApiUserPayload,
  ICustomAction,
  ICustomActionPayload,
  IDeleteSchemaPayload,
  IEditMerchantOrganizationData,
  IEditMerchantPayload,
  IGetMerchantOutputChannelsResult,
  IGetMerchantRoutingRulesetsPayload,
  IGetMerchantRoutingRulesetsResult,
  IMerchant,
  IMerchantClientsLimits,
  IMerchantClientsLimitsPayload,
  IMerchantDetails,
  IMerchantRoutingSchemas,
  IRoutingSchema,
  IRoutingSchemaPayload,
  IUpdateMerchantSingleParameterPayload,
  IVerifyMerchantOutputChannelPayload,
  TCustomActionData,
} from './types';

const merchantsAPI = {
  async getAllMerchants(
    filters: IAllPageFilters,
    viewOnlyActive: boolean,
  ): Promise<IPageType<IMerchant>> {
    const resultFilters = { ...filters };

    if (viewOnlyActive) {
      resultFilters.search = {
        ...filters.search,
        enabled: { operation: FILTER_OPERATIONS.EQUALS, value: true },
      };
    }

    const normalizedFilter = normalizeSearchFiltersPayload(resultFilters);

    const response = await axiosInstance.get('merchants/terminals', {
      params: normalizedFilter,
    });

    return normalizePageData<IMerchantData, IMerchant>(
      response.data,
      normalizeMerchant,
      JSON.stringify(filters.search),
    );
  },

  async getMerchant(id: number | string): Promise<IMerchantDetails> {
    const response = await axiosInstance.get(`merchants/terminals/${id}`);

    return normalizeMerchantDetails(response.data);
  },

  async getMerchantCascadingSchemaList(id: number | string): Promise<ICascadingSchema[]> {
    const response = await axiosInstance.get(
      `merchants/terminals/${id}/cascading-schemas`,
    );

    return response?.data?.result?.map(normalizeCascadingSchema) || [];
  },

  async getMerchantRoutingSchemaList(id: number | string): Promise<IRoutingSchema[]> {
    const response = await axiosInstance.get(`merchants/terminals/${id}/routing-schemas`);

    return response?.data?.result?.map(normalizeRoutingSchema) || [];
  },

  async getMerchantNames(): Promise<ISelectOption[]> {
    const response = await axiosInstance.get('merchants/terminals/names');

    return normalizeMerchantsNames(response.data);
  },

  async getMerchantsScope<T>(isTree?: boolean): Promise<T> {
    const response = await axiosInstance.get('merchants/scope');

    if (isTree) {
      const scope = response.data?.listMerchantAndMerchantTerminalNames?.map(
        normalizeMerchantsTreeScope,
      );

      return scope || [];
    }

    return normalizeMerchantsSelectScope(
      response.data?.listMerchantAndMerchantTerminalNames,
    ) as unknown as T;
  },

  async getMerchantRoutingSchemas(id: number): Promise<IMerchantRoutingSchemas[]> {
    const response = await axiosInstance.get(`merchants/terminals/${id}/routing-schemas`);

    return response?.data?.result?.map(normalizeRoutingSchemas) || [];
  },

  async getMerchantCountryGroups(filters?: IAllPageFilters): Promise<ISelectOption[]> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const response = await axiosInstance.get(`merchants/country-groups`, {
      params: { search },
    });

    return response?.data?.map(normalizeNames) || [];
  },

  async getMerchantTerminalCountryGroups(id: number): Promise<ISelectOption[]> {
    const response = await axiosInstance.get(`merchants/terminals/${id}/country-groups`);

    return response?.data?.result?.map(normalizeNames) || [];
  },

  async getMerchantBinGroups(filters?: IAllPageFilters): Promise<ISelectOption[]> {
    const { search } = normalizeSearchFiltersPayload(filters);
    const response = await axiosInstance.get(`merchants/bin-groups`, {
      params: { search },
    });

    return response?.data?.map(normalizeNames) || [];
  },

  async getMerchantTerminalBinGroups(id: number): Promise<ISelectOption[]> {
    const response = await axiosInstance.get(`merchants/terminals/${id}/bin-groups`);

    return response?.data?.result?.map(normalizeNames) || [];
  },

  async getMerchantGroups(id: number): Promise<ISelectOption[]> {
    const response = await axiosInstance.get(`merchant-terminal-groups/names`, {
      params: { merchantTerminalId: id },
    });

    return response?.data?.merchantGroupNames?.map(normalizeGroupNames) || [];
  },

  async updateMerchant({ id, payload }: IEditMerchantPayload): Promise<IBaseGist> {
    const normalizedData = prepareFormData(payload);

    const result = await axiosInstance.put(`merchants/terminals/${id}`, normalizedData);

    return normalizeBaseGist(result.data);
  },

  async createMerchant(payload: IMerchantForm): Promise<IBaseGist> {
    const normalizedData = prepareFormData(payload);

    const result = await axiosInstance.post(`merchants/terminals`, normalizedData);

    return normalizeBaseGist(result.data);
  },

  async copyMerchant({ merchantId, ...payload }: ICopyMerchantForm): Promise<IBaseGist> {
    const normalizedData = prepareFormData(payload);

    const result = await axiosInstance.post(
      `merchants/terminals/${merchantId}`,
      normalizedData,
    );

    return normalizeBaseGist(result.data);
  },

  async getMerchantCascadingSchema(id: number): Promise<ICascadingSchemaDetails> {
    const response = await axiosInstance.get(
      `/merchants/terminals/cascading-schemas/${id}`,
    );

    return normalizeCascadingSchemaDetails(response.data);
  },

  async createMerchantCascadingSchema({
    id,
    payload,
  }: ICascadingSchemaPayload): Promise<AxiosResponse> {
    const normalized = prepareFormData(payload);
    const result = await axiosInstance.post(
      `merchants/terminals/${id}/cascading-schemas`,
      normalized,
    );
    return result.data;
  },

  async updateMerchantCascadingSchema({
    id,
    payload,
  }: ICascadingSchemaPayload): Promise<AxiosResponse> {
    const normalized = prepareFormData(payload);
    const result = await axiosInstance.put(
      `merchants/terminals/cascading-schemas/${id}`,
      normalized,
    );
    return result.data;
  },

  async deleteMerchantCascadingSchema({
    schemaId,
  }: IDeleteSchemaPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(
      `merchants/terminals/cascading-schemas/${schemaId}`,
    );

    return result;
  },

  async activateCascadingSchema({
    id,
    payload,
  }: IActivateCascadingSchemaPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.patch(
      `/merchants/terminals/${id}/cascading-schemas`,
      payload,
      { params: { active: true } },
    );

    return result.data;
  },

  async createMerchantRoutingSchema({
    id,
    payload,
  }: IRoutingSchemaPayload): Promise<AxiosResponse> {
    const normalized = prepareFormData(payload);
    const result = await axiosInstance.post(
      `merchants/terminals/${id}/routing-schemas`,
      normalized,
    );
    return result.data;
  },

  async updateMerchantRoutingSchema({
    id,
    payload,
  }: IRoutingSchemaPayload): Promise<AxiosResponse> {
    const normalized = prepareFormData({ name: payload.name });
    const result = await axiosInstance.put(
      `merchants/terminals/routing-schemas/${id}`,
      normalized,
    );
    return result.data;
  },

  async deleteMerchantRoutingSchema({
    schemaId,
  }: IDeleteSchemaPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(
      `merchants/terminals/routing-schemas/${schemaId}`,
    );

    return result;
  },

  async activateRoutingSchema({
    activate,
    id,
  }: IActivateRoutingSchemaPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.patch(
      `/merchants/terminals/routing-schemas/${id}`,
      null,
      { params: { active: activate } },
    );

    return result.data;
  },

  async getApiUsers(id: string): Promise<IApiUser[]> {
    const response = await axiosInstance.get(`merchant-terminal/${id}/api-users`);

    return response.data?.result?.map(normalizedApiUser) || [];
  },

  async createApiUser(payload: ICreateApiUserPayload): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post('api-users', normalizedPayload);

    return response.data;
  },

  async deleteApiUser(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`api-users/${id}`);

    return response.data;
  },

  async deleteMerchant(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`/merchants/terminals/${id}`);

    return response.data;
  },

  async fullDeleteMerchant(id: number | string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`/merchants/terminals/full/${id}`);

    return response.data;
  },

  async updateMerchantSingleParameter({
    id,
    settings,
  }: IUpdateMerchantSingleParameterPayload): Promise<AxiosResponse> {
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(`/merchants/terminals/${id}`, null, {
      params: normalizedSettings,
    });

    return response.data;
  },

  async editApiUser({ id, settings }: IApiUserEdit): Promise<AxiosResponse> {
    const normalizedSettings = prepareFormData(settings);

    const response = await axiosInstance.patch(`api-users/${id}`, null, {
      params: normalizedSettings,
    });

    return response;
  },

  async getMerchantClientsLimits(id: number | string): Promise<IMerchantClientsLimits> {
    const response = await axiosInstance.get(`merchants/terminals/${id}/customer-limits`);

    return normalizeMerchantClientsLimits(response.data);
  },

  async createMerchantClientsLimits({
    merchantTerminalId,
    payload,
  }: IMerchantClientsLimitsPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `merchants/terminals/${merchantTerminalId}/customer-limits`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editMerchantClientsLimits({
    merchantTerminalId,
    payload,
  }: IMerchantClientsLimitsPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `merchants/terminals/${merchantTerminalId}/customer-limits`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteMerchantClientsLimits(merchantTerminalId: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(
      `/merchants/terminals/${merchantTerminalId}/customer-limits`,
    );

    return response.data;
  },

  async editMerchantOrganization(
    payload: IEditMerchantOrganizationData,
    id?: string,
  ): Promise<AxiosResponse> {
    const normalizedData = prepareFormData(payload);

    const response = await axiosInstance.post(
      `/merchants/terminals/${id}/transfer`,
      normalizedData,
    );
    return response.data;
  },

  async getMerchantRoutingRulesetsList({
    id,
    ...params
  }: IGetMerchantRoutingRulesetsPayload): Promise<IGetMerchantRoutingRulesetsResult> {
    const response = await axiosInstance.get(
      `/merchants/terminals/${id}/routing-rulesets`,
      {
        params,
      },
    );

    return normalizeMerchantRulesets(response.data);
  },

  async getMerchantOutputChannelsList(
    id: number | string,
  ): Promise<IGetMerchantOutputChannelsResult> {
    const response = await axiosInstance.get(`/merchants/terminals/${id}/channels`);

    return normalizeMerchantOutputChannelsList(response.data);
  },

  async getMerchantOutputChannels(
    filters: IAllPageFilters,
  ): Promise<IPageType<IOutputChannel>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('/merchants/terminals/channels', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IOutputChannel>(
      response.data,
      normalizeChannel,
      JSON.stringify(filters.search),
    );
  },

  async getMerchantOutputChannel(id: number): Promise<IOutputChannel> {
    const response = await axiosInstance.get(`/merchants/terminals/channels/${id}`);
    return normalizeChannel(response.data);
  },

  async createMerchantOutputChannel({
    payload,
  }: IOutputChannelPayload): Promise<IBaseGist> {
    const { merchantTerminalId, ...others } = payload;
    const normalizedPayload = prepareFormData(others);

    const response = await axiosInstance.post(
      `/merchants/terminals/${merchantTerminalId}/channels`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editMerchantOutputChannel({
    id,
    payload,
  }: IOutputChannelPayload): Promise<IBaseGist> {
    const { merchantTerminalId: _, ...others } = payload;
    const normalizedPayload = prepareFormData(others);

    const response = await axiosInstance.put(
      `/merchants/terminals/channels/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async verifyOutputChannel({
    id,
    otp,
  }: IVerifyMerchantOutputChannelPayload): Promise<IBaseGist> {
    const response = await axiosInstance.put(
      `/merchants/terminals/channels/${id}/confirmation/${otp}`,
    );

    return normalizeBaseGist(response?.data);
  },

  async resendOtp(id: number | string): Promise<AxiosResponse> {
    const result = await axiosInstance.post(
      `/merchants/terminals/channels/${id}/confirmation`,
    );

    return result;
  },

  async deleteMerchantOutputChannel(id: number | string): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`/merchants/terminals/channels/${id}`);

    return result;
  },

  async groupEditMerchants({
    filters,
    settings,
  }: IGroupEditPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.patch('merchants/terminals', settings, {
      params: { search },
    });

    return response;
  },

  async getMerchantCustomActions(
    filters: IAllPageFilters,
  ): Promise<IPageType<ICustomAction>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('/merchants/terminals/actions', {
      params: normalizedFilter,
    });

    return normalizePageData<TCustomActionData, ICustomAction>(
      response.data,
      normalizeCustomAction,
      JSON.stringify(filters.search),
    );
  },

  async getMerchantCustomAction(id: number): Promise<ICustomAction> {
    const response = await axiosInstance.get(`/merchants/terminals/actions/${id}`);
    return normalizeCustomAction(response.data);
  },

  async createMerchantCustomAction({
    payload,
  }: ICustomActionPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(
      `/merchants/terminals/actions`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async editMerchantCustomAction({
    id,
    payload,
  }: ICustomActionPayload): Promise<IBaseGist> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(
      `/merchants/terminals/actions/${id}`,
      normalizedPayload,
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteMerchantCustomAction(id: INullable<number>): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`/merchants/terminals/actions/${id}`);

    return result;
  },
};

export default merchantsAPI;
